/**
 * Buying AI transcription seats from the public pricing page.
 *
 * The seat add-ons on that page used to send a demo request, which asked a practice already using
 * CatTrax to wait for a phone call before it could buy a seat. This modal lets them buy on the
 * spot: sign in, pick the practice, choose the seats, put a card on file and confirm.
 *
 * Authentication comes first because seats belong to a practice, and nothing about a practice can
 * be read from a public page. The app's own payment settings dialog is deliberately never opened
 * from the website - it carries the token wallet, top-ups and usage history, none of which belong
 * in front of someone who came to the pricing page to buy a seat. Handing the seats out afterwards
 * is its own modal again, seatAssignController.
 *
 * Every figure shown comes back from the server. No price, proration or monthly total is worked
 * out here, because proration cannot be reproduced client side (see TranscriptionSeatsAPI).
 */
app.controller("seatPurchaseController", [
    "$scope",
    "$q",
    "$timeout",
    "$rootScope",
    "$uibModalInstance",
    "AuthService",
    "UserService",
    "Session",
    "AUTHENTICATION_STATE",
    "TranscriptionSeatsAPI",
    "PaymentTransactionsAPI",
    "ENV",
    "countryConfig",
    "selectedTier",
    function (
        $scope,
        $q,
        $timeout,
        $rootScope,
        $uibModalInstance,
        AuthService,
        UserService,
        Session,
        AUTHENTICATION_STATE,
        TranscriptionSeatsAPI,
        PaymentTransactionsAPI,
        ENV,
        countryConfig,
        selectedTier
    ) {
        /** The tier whose button was clicked, so the draft opens on the seat they came here for. */
        $scope.selectedTier = selectedTier || null;

        $scope.step = "credentials";
        $scope.isBusy = false;
        $scope.errorMessage = null;
        $scope.successMessage = null;

        $scope.creds = { keepLoggedIn: true };
        $scope.codeDigits = ["", "", "", "", "", ""];
        $scope.secretCode = "";
        $scope.practices = [];

        /** Which card the card step is editing: PRIMARY or BACKUP. */
        $scope.cardTarget = "PRIMARY";

        $scope.ENVIRONMENT = ENV.ENVIRONMENT;
        $scope.Session = Session;
        $scope.API = TranscriptionSeatsAPI;
        $scope.CARD_API = PaymentTransactionsAPI;
        $scope.fromCents = TranscriptionSeatsAPI.fromCents;
        $scope.formatAllowanceHours = TranscriptionSeatsAPI.formatAllowanceHours;

        // Seat state, all of it loaded once the practice is known.
        $scope.plans = [];
        $scope.currency = null;
        $scope.subscription = null;
        $scope.wallet = null;
        $scope.preview = null;
        $scope.seatDraft = {};
        $scope.isPreviewing = false;
        $scope.isLoadingSeats = false;

        var csrfToken = null;
        var mfaParameters = {};
        var mfaPathway = "";

        /** What the practice owns today, so a change can be detected without re-reading the draft. */
        var committedSeats = {};

        /** Whether the clicked tier has already been added to the draft. See seedSeatDraft. */
        var hasSeededDraft = false;

        /**
         * Held across a retry so a timeout on a purchase cannot charge the practice twice. Cleared
         * only once the server has accepted the change.
         */
        var requestId = null;

        var previewTimer = null;
        var previewSequence = 0;

        var stripe = null;
        var elements = null;
        var cardElement = null;

        // =================================================================
        // Step plumbing
        // =================================================================

        function setStep(step) {
            $scope.step = step;
            $scope.errorMessage = null;
        }

        $scope.getTitle = function () {
            switch ($scope.step) {
                case "credentials":
                    return "Sign in to add seats";
                case "mfaVerify":
                    return "Two-Factor Authentication";
                case "mfaEnrolStart":
                    return "Security Upgrade";
                case "mfaEnrolVerify":
                    return "Scan QR Code";
                case "practice":
                    return "Choose a practice";
                case "addCard":
                    return $scope.isReplacingCard() ? "Change the " + $scope.getCardTargetLabel() + " card" : "Add a payment card";
                case "blocked":
                    return "Not available yet";
                case "done":
                    return "Seats added";
                default:
                    return "AI Transcription Seats";
            }
        };

        $scope.close = function () {
            $uibModalInstance.dismiss("cancel");
        };

        // =================================================================
        // Sign in
        // =================================================================

        /**
         * Written out here rather than shared with loginPanelController because only two of that
         * panel's seven states are wanted: a visitor who has to reset a password, or is still on a
         * temporary one, is better served by the real login page and is sent there.
         */
        $scope.signIn = function (isValid) {
            if (!isValid || $scope.isBusy) {
                return;
            }

            $scope.isBusy = true;
            $scope.errorMessage = null;
            $scope.successMessage = null;

            AuthService.authorize()
                .then(function (token) {
                    csrfToken = token;
                    return AuthService.login($scope.creds, csrfToken, onAuthEvent);
                })
                .then(function () {
                    // Neither MFA branch fired, so AuthService has handed back no state and there
                    // is no session to buy against. onAuthEvent runs synchronously inside the login
                    // call above, so by here it has already had its say.
                    if ($scope.step === "credentials" && !$scope.errorMessage) {
                        $scope.isBusy = false;
                        $scope.errorMessage =
                            "We could not complete the sign in from this page. Please log in to CatTrax and add seats from Payment Settings.";
                    }
                })
                .catch(function () {
                    $timeout(function () {
                        $scope.isBusy = false;
                        $scope.errorMessage = "The email or password you entered is incorrect.";
                    });
                });
        };

        /**
         * Applied synchronously rather than through a $timeout, because signIn checks straight
         * afterwards whether a state came back at all - a deferred update would still read as
         * "nothing happened" and put a spurious failure in front of a sign in that worked.
         */
        function onAuthEvent(creds, authEvent) {
            mfaParameters = angular.extend({}, authEvent, { creds: creds });
            $scope.isBusy = false;

            // A temporary password has to be replaced before anything can be bought, and that form
            // belongs on the login page rather than in a purchase modal.
            if (authEvent.updatePasswordRequired) {
                $scope.errorMessage =
                    "Your password needs updating before you can buy seats. Please log in to CatTrax first, then add seats from Payment Settings.";
                return;
            }

            switch (authEvent.authenticationState) {
                case AUTHENTICATION_STATE.waitingMFACode:
                    $scope.codeDigits = ["", "", "", "", "", ""];
                    setStep("mfaVerify");
                    focusFirstDigit();
                    break;

                case AUTHENTICATION_STATE.configMFA:
                    setStep("mfaEnrolStart");
                    break;

                default:
                    break;
            }
        }

        function focusFirstDigit() {
            $timeout(function () {
                var first = document.getElementById("seat-mfa-digit-0");
                if (first) {
                    first.focus();
                }
            }, 200);
        }

        $scope.onDigitInput = function (index, pathway) {
            var value = ($scope.codeDigits[index] || "").replace(/\D/, "").substr(0, 1);
            $scope.codeDigits[index] = value;
            mfaPathway = pathway;

            if (value && index < $scope.codeDigits.length - 1) {
                $timeout(function () {
                    var next = document.getElementById("seat-mfa-digit-" + (index + 1));
                    if (next) {
                        next.focus();
                    }
                }, 0);
            }
        };

        $scope.clearCodeDigit = function (index) {
            $scope.codeDigits.splice(index, 1, "");
        };

        $scope.onDigitKeyDown = function (keyEvent, index) {
            var BACKSPACE_KEY_CODE = 8;

            if ((keyEvent.which || keyEvent.keyCode) !== BACKSPACE_KEY_CODE) {
                return;
            }

            if (($scope.codeDigits[index] || "") === "" && index > 0) {
                keyEvent.preventDefault();
                $timeout(function () {
                    var previous = document.getElementById("seat-mfa-digit-" + (index - 1));
                    if (previous) {
                        previous.focus();
                    }
                }, 0);
            }
        };

        $scope.onCodePaste = function ($event, pathway) {
            $event.preventDefault();

            var rawEvent = $event.originalEvent || $event;
            var clipboard = rawEvent.clipboardData || window.clipboardData;

            if (!clipboard || !clipboard.getData) {
                return;
            }

            var pasted = (clipboard.getData("text/plain") || clipboard.getData("Text") || "").replace(/\D/g, "").substr(0, 6);

            if (pasted.length !== 6) {
                return;
            }

            for (var i = 0; i < 6; i++) {
                $scope.codeDigits[i] = pasted.charAt(i);
            }

            $scope.onDigitInput(5, pathway);
        };

        // Submits itself the moment the sixth box is filled, so a pasted or typed code needs no
        // second action. Matches the login panel, where the same six boxes behave this way.
        $scope.$watchCollection("codeDigits", function (digits) {
            var isComplete = digits.every(function (digit) {
                return digit && digit.length === 1;
            });

            if (!isComplete || $scope.isBusy) {
                return;
            }

            if (mfaPathway === "verify") {
                $scope.verifyMfa(digits.join(""));
            }

            if (mfaPathway === "config") {
                $scope.completeMfaEnrolment(digits.join(""));
            }
        });

        $scope.verifyMfa = function (code) {
            $scope.isBusy = true;
            $scope.errorMessage = null;

            AuthService.verifyMfaCode(mfaParameters.creds, csrfToken, code)
                .then(function () {
                    return AuthService.exchangeForTokens(mfaParameters.creds.keepLoggedIn || false);
                })
                .then(function (tokenResponse) {
                    Session.create(tokenResponse.data.access_token, tokenResponse.data.expires_in, tokenResponse.headers("X-USER-ID"));

                    // The visitor is now logged in while still on a public page, so the app's
                    // inactivity watch has to start here rather than at the welcome screen.
                    $rootScope.startIdleTimer();

                    return onAuthenticated();
                })
                .catch(function () {
                    $timeout(function () {
                        $scope.isBusy = false;
                        $scope.codeDigits = ["", "", "", "", "", ""];
                        $scope.errorMessage = "That code is not correct. Please try again.";
                        focusFirstDigit();
                    });
                });
        };

        $scope.startMfaEnrolment = function () {
            $scope.isBusy = true;
            $scope.errorMessage = null;

            AuthService.initialMfa(csrfToken)
                .then(function (response) {
                    $timeout(function () {
                        $scope.secretCode = "otpauth://totp/CatTrax?secret=" + response.data.secret_code;
                        $scope.codeDigits = ["", "", "", "", "", ""];
                        setStep("mfaEnrolVerify");
                        focusFirstDigit();
                    });
                })
                .catch(function () {
                    $timeout(function () {
                        setStep("credentials");
                        $scope.errorMessage = "We couldn't start the 2FA setup. Please try signing in again.";
                    });
                })
                .finally(function () {
                    $timeout(function () {
                        $scope.isBusy = false;
                    });
                });
        };

        $scope.completeMfaEnrolment = function (code) {
            $scope.isBusy = true;
            $scope.errorMessage = null;

            AuthService.completeMfa(code, csrfToken)
                .then(function () {
                    $timeout(function () {
                        $scope.creds = { keepLoggedIn: true };
                        setStep("credentials");
                        $scope.successMessage = "2FA setup complete. Please sign in to continue.";
                    });
                })
                .catch(function () {
                    $timeout(function () {
                        $scope.codeDigits = ["", "", "", "", "", ""];
                        $scope.errorMessage = "That code is not correct. Please try again.";
                        focusFirstDigit();
                    });
                })
                .finally(function () {
                    $timeout(function () {
                        $scope.isBusy = false;
                    });
                });
        };

        // =================================================================
        // Practice selection
        // =================================================================

        /**
         * Seats are owned by a practice, so one has to be settled on before any seat call can be
         * made - every seat and wallet request carries an X-PRACTICE-ID header.
         */
        function onAuthenticated() {
            return UserService.getUser()
                .then(function (userResponse) {
                    Session.setCurrentUser(userResponse.data);
                    return UserService.getUserPractices();
                })
                .then(function (practicesResponse) {
                    Session.setPracticeOptions(practicesResponse.data || []);
                    $scope.practices = Session.practiceOptions;

                    if ($scope.practices.length === 1) {
                        return $scope.choosePractice($scope.practices[0]);
                    }

                    if (!$scope.practices.length) {
                        $timeout(function () {
                            $scope.isBusy = false;
                            $scope.errorMessage = "This login is not attached to a practice, so there is nothing to add seats to.";
                        });
                        return null;
                    }

                    $timeout(function () {
                        $scope.isBusy = false;
                        setStep("practice");
                    });
                    return null;
                })
                .catch(function () {
                    $timeout(function () {
                        $scope.isBusy = false;
                        $scope.errorMessage = "We signed you in but could not read your practice details. Please try again shortly.";
                    });
                });
        }

        /** Whether there is more than one practice to choose between. */
        $scope.hasPracticeChoice = function () {
            return $scope.practices.length > 1;
        };

        /**
         * Back to the picker, so a group can set its practices up one after another rather than
         * being held on whichever one it happened to land on at sign in.
         */
        $scope.changePractice = function () {
            $scope.successMessage = null;
            setStep("practice");
        };

        /** Only offered once a practice is already loaded, so leaving the picker has a way back. */
        $scope.cancelPracticeChange = function () {
            setStep($scope.plans.length ? "purchase" : "credentials");
        };

        $scope.choosePractice = function (practice) {
            Session.setPractice(practice);
            resetSeatState();

            if (!isTranscriptionEnabled()) {
                // Signed in, so there is nothing useful to send them back to - the sign in form
                // would only invite them to do the same thing again.
                $scope.isBusy = false;
                setStep("blocked");
                $scope.errorMessage =
                    "AI transcription is not switched on for " +
                    (practice && practice.name ? practice.name : "this practice") +
                    " yet. Contact CatTrax and we will enable it, then your seats can be added here.";
                return null;
            }

            return loadSeats();
        };

        /**
         * Whether seats can be bought at all. Settled by which site this is - every NZ practice
         * has AI transcription and no AU practice does - rather than by a per-practice flag, so
         * it does not change when the practice does.
         */
        function isTranscriptionEnabled() {
            return countryConfig.transcriptionFeatureEnabled;
        }

        /**
         * Clears everything read for the practice being left behind, so nothing from it can be
         * shown against, or bought for, the next one.
         *
         * The idempotency key goes with it. It stands for one particular change to one particular
         * subscription, and reusing it across practices is exactly the collision it exists to
         * prevent.
         */
        function resetSeatState() {
            $scope.plans = [];
            $scope.currency = null;
            $scope.subscription = null;
            $scope.wallet = null;
            $scope.preview = null;
            $scope.seatDraft = {};
            $scope.successMessage = null;

            committedSeats = {};
            hasSeededDraft = false;
            requestId = null;

            // Any preview still in flight is for the previous practice, so its reply is ignored -
            // and so is its finally, which would otherwise have cleared the flag.
            previewSequence++;
            $scope.isPreviewing = false;

            if (previewTimer) {
                $timeout.cancel(previewTimer);
                previewTimer = null;
            }
        }

        // =================================================================
        // Loading the seat product
        // =================================================================

        function loadSeats() {
            $scope.isBusy = false;
            $scope.isLoadingSeats = true;
            setStep("purchase");

            return $q
                .all({
                    plans: TranscriptionSeatsAPI.getSeatPlans(),
                    subscription: TranscriptionSeatsAPI.getSubscription(),
                    wallet: PaymentTransactionsAPI.getWalletStatus(),
                })
                .then(function (responses) {
                    var plansResponse = responses.plans.data || {};

                    $scope.plans = plansResponse.plans || [];
                    $scope.currency = plansResponse.currency;
                    $scope.subscription = responses.subscription.data || null;
                    $scope.wallet = responses.wallet.data || null;

                    seedSeatDraft();
                    requestPreview();
                })
                .catch(function (error) {
                    $scope.errorMessage = TranscriptionSeatsAPI.getErrorMessage(
                        error,
                        "Could not load seat pricing for your practice. Please try again shortly."
                    );
                })
                .finally(function () {
                    $timeout(function () {
                        $scope.isLoadingSeats = false;
                        initStripe();
                    });
                });
        }

        /**
         * Opens on what the practice already owns, plus the one seat the clicked button was asking
         * for. A practice that already holds that tier is therefore adding to it, rather than being
         * shown a count it would have to correct.
         *
         * Run again after a purchase to take the server's new counts as the baseline. The clicked
         * tier is only added the first time: repeating it there would put a second unasked-for seat
         * in the box of someone who has just bought one.
         */
        function seedSeatDraft() {
            committedSeats = {};

            $scope.plans.forEach(function (plan) {
                var owned = $scope.getAllocation(plan.tier).quantity_owned;
                committedSeats[plan.tier] = owned;
                $scope.seatDraft[plan.tier] = owned;
            });

            if (!hasSeededDraft && $scope.selectedTier && $scope.seatDraft[$scope.selectedTier] != null) {
                $scope.seatDraft[$scope.selectedTier] = $scope.seatDraft[$scope.selectedTier] + 1;
            }

            hasSeededDraft = true;
        }

        // =================================================================
        // Reading the subscription
        // =================================================================

        $scope.getAllocation = function (tier) {
            return TranscriptionSeatsAPI.getAllocation($scope.subscription, tier);
        };

        $scope.isSubscriptionLive = function () {
            return TranscriptionSeatsAPI.isSubscriptionLive($scope.subscription);
        };

        $scope.hasSubscription = function () {
            return !!$scope.subscription && $scope.subscription.status !== "NONE";
        };

        /**
         * Whether the practice is billed by invoice under an enterprise agreement. Seats are then
         * granted by CatTrax rather than bought by card, so the purchase controls step aside.
         */
        $scope.isInvoiceBilled = function () {
            return !!$scope.subscription && $scope.subscription.billing_type === "INVOICE" && $scope.subscription.status !== "CANCELED";
        };

        /**
         * Whether a card exists that a seat invoice can be charged to. Only the primary counts: the
         * subscription is created against the practice's default payment method, and Stripe never
         * reaches for the backup.
         */
        $scope.hasPrimaryCard = function () {
            if ($scope.wallet && $scope.wallet.default_payment_method_id) {
                return true;
            }

            return !!$scope.subscription && $scope.subscription.has_default_payment_method === true;
        };

        $scope.getTotalOwned = function () {
            return $scope.plans.reduce(function (total, plan) {
                return total + $scope.getAllocation(plan.tier).quantity_owned;
            }, 0);
        };

        // =================================================================
        // Editing quantities
        // =================================================================

        $scope.adjust = function (tier, delta) {
            $scope.setQuantity(tier, ($scope.seatDraft[tier] || 0) + delta);
        };

        $scope.setQuantity = function (tier, quantity) {
            var next = parseInt(quantity, 10);

            if (isNaN(next) || next < 0) {
                next = 0;
            }

            $scope.seatDraft[tier] = next;
            $scope.onSeatDraftChanged();
        };

        /**
         * Steps down only as far as the seats already handed out, so the control itself cannot
         * produce a change the server would reject.
         */
        $scope.getMinimumQuantity = function (tier) {
            if (!$scope.isSubscriptionLive()) {
                return 0;
            }

            return $scope.getAllocation(tier).quantity_assigned;
        };

        $scope.getTotalSeats = function () {
            return $scope.plans.reduce(function (total, plan) {
                return total + ($scope.seatDraft[plan.tier] || 0);
            }, 0);
        };

        $scope.hasSeatChanges = function () {
            return $scope.plans.some(function (plan) {
                return ($scope.seatDraft[plan.tier] || 0) !== (committedSeats[plan.tier] || 0);
            });
        };

        /**
         * Whether the draft raises any tier above what is already paid for, which is the case that
         * bills immediately and so the case that needs a card.
         */
        $scope.isIncrease = function () {
            return $scope.plans.some(function (plan) {
                return ($scope.seatDraft[plan.tier] || 0) > $scope.getAllocation(plan.tier).quantity_owned;
            });
        };

        /** Mirrors the server's own rules, so a visitor is told what is wrong while still editing. */
        $scope.getValidationMessage = function () {
            var total = $scope.getTotalSeats();

            if (total < 1) {
                return $scope.isSubscriptionLive()
                    ? "A subscription must keep at least one seat."
                    : "Choose at least one seat to start a subscription.";
            }

            var stranded = $scope.plans.filter(function (plan) {
                return ($scope.seatDraft[plan.tier] || 0) < $scope.getAllocation(plan.tier).quantity_assigned;
            });

            if (stranded.length) {
                return "Some of those seats are in use. Release them from your team in CatTrax before reducing them.";
            }

            if ($scope.isIncrease() && !$scope.hasPrimaryCard()) {
                return "Add a card before buying seats.";
            }

            return null;
        };

        $scope.canConfirm = function () {
            return (
                $scope.hasSeatChanges() &&
                !$scope.getValidationMessage() &&
                !$scope.isPreviewing &&
                !$scope.isLoadingSeats &&
                !TranscriptionSeatsAPI.isProcessing
            );
        };

        // =================================================================
        // Pricing the change
        // =================================================================

        $scope.onSeatDraftChanged = function () {
            $scope.errorMessage = null;

            // The idempotency key stands for one particular change. A different draft is a
            // different change, and reusing the key would have the server replay the old one.
            requestId = null;

            // Held until the debounced preview lands, so the price on screen can never be for a
            // different quantity than the one Confirm would send.
            $scope.isPreviewing = true;

            if (previewTimer) {
                $timeout.cancel(previewTimer);
            }

            // Debounced because each preview is a live call through to Stripe.
            previewTimer = $timeout(requestPreview, 500);
        };

        function requestPreview() {
            // An invoice billed practice never prices seats through Stripe - the server rejects the
            // preview, and the purchase controls are hidden for it anyway.
            if ($scope.isInvoiceBilled() || $scope.getTotalSeats() < 1) {
                $scope.preview = null;
                $scope.isPreviewing = false;
                return;
            }

            var sequence = ++previewSequence;
            $scope.isPreviewing = true;

            return TranscriptionSeatsAPI.previewSubscription(toSeatQuantities())
                .then(function (response) {
                    // A slower earlier preview must never overwrite a newer one.
                    if (sequence !== previewSequence) {
                        return;
                    }

                    $scope.preview = response.data || null;
                })
                .catch(function (error) {
                    if (sequence !== previewSequence) {
                        return;
                    }

                    $scope.preview = null;
                    $scope.errorMessage = TranscriptionSeatsAPI.getErrorMessage(error, "Could not price that change. Please try again.");
                })
                .finally(function () {
                    if (sequence === previewSequence) {
                        $timeout(function () {
                            $scope.isPreviewing = false;
                        });
                    }
                });
        }

        function toSeatQuantities() {
            return $scope.plans.map(function (plan) {
                return { tier: plan.tier, quantity: $scope.seatDraft[plan.tier] || 0 };
            });
        }

        $scope.getPreviewLine = function (tier) {
            if (!$scope.preview || !$scope.preview.lines) {
                return null;
            }

            return $scope.preview.lines.filter(function (line) {
                return line.tier === tier;
            })[0];
        };

        // =================================================================
        // Card
        // =================================================================

        function initStripe() {
            // index.html loads Stripe async, so it can still be absent when this modal opens.
            if (stripe || typeof Stripe === "undefined") {
                return;
            }

            stripe = Stripe(ENV.STRIPE_CARD_INITIATE_KEY);
            elements = stripe.elements();
        }

        $scope.hasBackupCard = function () {
            return !!($scope.wallet && $scope.wallet.backup_payment_method_id);
        };

        $scope.getCardTargetLabel = function () {
            return $scope.cardTarget === "BACKUP" ? "backup" : "primary";
        };

        /** Whether the card step is replacing a card rather than putting the first one on file. */
        $scope.isReplacingCard = function () {
            return $scope.cardTarget === "BACKUP" ? $scope.hasBackupCard() : $scope.hasPrimaryCard();
        };

        /**
         * Changing the primary card repoints what the practice is billed on from here: the wallet,
         * top-ups, and any seat subscription started afterwards.
         *
         * A seat subscription that already exists is a different matter. Stripe pins the payment
         * method onto the subscription when it is created, and nothing in the API moves a live one
         * onto a new card - so its invoices, including the proration for seats added on this very
         * screen, still go to the card it was set up with. That is said plainly on screen rather
         * than left for a practice to discover on an invoice.
         */
        $scope.isCardChangeLimitedBySubscription = function () {
            return $scope.cardTarget === "PRIMARY" && $scope.hasPrimaryCard() && $scope.isSubscriptionLive();
        };

        /**
         * @param {string} [target] PRIMARY or BACKUP. Defaults to the primary, which is the only
         *   card a seat invoice can be charged to.
         */
        $scope.openCardForm = function (target) {
            initStripe();

            if (!stripe) {
                $scope.errorMessage = "The card form could not be loaded. Please reload the page and try again.";
                return;
            }

            $scope.cardTarget = target === "BACKUP" ? "BACKUP" : "PRIMARY";
            setStep("addCard");

            $timeout(function () {
                if (cardElement) {
                    cardElement.destroy();
                    cardElement = null;
                }

                mountCardElement();
            }, 150);
        };

        function mountCardElement() {
            if (cardElement) {
                return;
            }

            cardElement = elements.create("card", {
                style: {
                    base: {
                        color: "#333333",
                        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                        fontSize: "16px",
                        "::placeholder": { color: "#aab7c4" },
                    },
                    invalid: { color: "#a94442", iconColor: "#a94442" },
                },
                hidePostalCode: true,
            });

            cardElement.mount("#seat-card-element");

            cardElement.on("change", function (event) {
                $scope.$apply(function () {
                    $scope.errorMessage = event.error ? event.error.message : null;
                });
            });
        }

        $scope.cancelAddCard = function () {
            setStep("purchase");
        };

        /**
         * Stores the card and points the practice at it. Replacing a card is the same call as
         * adding one - the server overwrites whichever slot is named - so no card is removed first,
         * and the old one is deliberately left attached in Stripe: detaching it would break any
         * subscription still billing against it.
         */
        $scope.saveCard = function () {
            var target = $scope.cardTarget;
            var wasReplacing = $scope.isReplacingCard();

            $scope.errorMessage = null;
            PaymentTransactionsAPI.isProcessing = true;

            PaymentTransactionsAPI.initiateCardSetup(Session.practice.id)
                .then(function (response) {
                    return stripe.confirmCardSetup(response.data.clientSecret, {
                        payment_method: {
                            card: cardElement,
                            billing_details: {
                                name: Session.user.firstName + " " + Session.user.lastName,
                                email: Session.user.email,
                            },
                        },
                    });
                })
                .then(function (result) {
                    if (result.error) {
                        throw new Error(result.error.message);
                    }

                    var paymentMethodId = result.setupIntent.payment_method;

                    return target === "BACKUP"
                        ? PaymentTransactionsAPI.setBackupPaymentMethod(paymentMethodId)
                        : PaymentTransactionsAPI.setDefaultPaymentMethod(paymentMethodId);
                })
                .then(function () {
                    return PaymentTransactionsAPI.getWalletStatus();
                })
                .then(function (walletResponse) {
                    $timeout(function () {
                        $scope.wallet = walletResponse.data || $scope.wallet;
                        setStep("purchase");
                        $scope.successMessage = wasReplacing
                            ? "Your " + (target === "BACKUP" ? "backup" : "primary") + " card has been changed."
                            : "Card saved.";

                        // A new card can turn a blocked purchase into a chargeable one, and the
                        // pricing on screen was worked out before it existed.
                        requestPreview();

                        $timeout(function () {
                            $scope.successMessage = null;
                        }, 5000);
                    });
                })
                .catch(function (error) {
                    $timeout(function () {
                        $scope.errorMessage =
                            (error && error.message) || "That card could not be saved. Please check the details and try again.";
                    });
                })
                .finally(function () {
                    $timeout(function () {
                        PaymentTransactionsAPI.isProcessing = false;
                    });
                });
        };

        // =================================================================
        // Confirming the purchase
        // =================================================================

        $scope.confirm = function () {
            if (!$scope.canConfirm()) {
                return;
            }

            $scope.errorMessage = null;
            TranscriptionSeatsAPI.isProcessing = true;

            if (!requestId) {
                requestId = TranscriptionSeatsAPI.newRequestId();
            }

            TranscriptionSeatsAPI.saveSubscription(toSeatQuantities(), requestId)
                .then(function (response) {
                    // The request succeeded, so a later change must not reuse this key.
                    requestId = null;

                    $timeout(function () {
                        $scope.subscription = response.data || $scope.subscription;
                        seedSeatDraft();
                        setStep("done");
                    });
                })
                .catch(function (error) {
                    // requestId is deliberately kept, so retrying this same change is safe.
                    $timeout(function () {
                        $scope.errorMessage = TranscriptionSeatsAPI.getErrorMessage(error, "Could not add those seats. Please try again.");
                    });
                })
                .finally(function () {
                    $timeout(function () {
                        TranscriptionSeatsAPI.isProcessing = false;
                    });
                });
        };

        /** Seats still have to be handed to clinicians, and that happens inside the app. */
        $scope.finish = function () {
            $uibModalInstance.close({ action: "ASSIGN_SEATS" });
        };

        // =================================================================
        // Entry
        // =================================================================

        /**
         * A session already in hand means there is nothing to sign in for: the modal has run once
         * this visit, or the assign modal has sent the practice back here for more seats.
         */
        if (Session.token && Session.practice) {
            // Taken from the session rather than re-fetched, so the practice switcher is there on
            // re-entry without another round trip.
            $scope.practices = Session.practiceOptions || [];

            if (isTranscriptionEnabled()) {
                loadSeats();
            } else {
                setStep("blocked");
                $scope.errorMessage =
                    "AI transcription is not switched on for " +
                    (Session.practice.name || "this practice") +
                    " yet. Contact CatTrax and we will enable it, then your seats can be added here.";
            }
        }

        $scope.$on("$destroy", function () {
            if (previewTimer) {
                $timeout.cancel(previewTimer);
            }

            if (cardElement) {
                cardElement.destroy();
                cardElement = null;
            }
        });
    },
]);
